import type { AiContext } from '@/components/chat/types/ai-context';
import type { OrderedFiles } from '@/lib/ai/ordered-files-service/order-files-prompts';
import { tool } from 'ai';
import { z } from 'zod';

import type { ChangedFileMatchKind } from './pr-context-tools/types';

import { resolveChangedFilePathFromOrder } from './pr-context-tools/helpers';

export const setPrFileViewedSchema = z.object({
  filenames: z
    .array(z.string().trim().min(1))
    .min(1)
    .optional()
    .describe(
      'Optional changed PR file paths to mark viewed or unviewed. Omit only when the user refers to the current file.',
    ),
  viewed: z.boolean().describe('True to mark viewed, false to mark unviewed'),
});

export type SetPrFileViewedInput = z.infer<typeof setPrFileViewedSchema>;

type FileMatchKind = ChangedFileMatchKind | 'current';

export type SetPrFileViewedResult = {
  pendingAction: true;
  success: boolean;
  viewed: boolean;
  filenames: string[];
  unresolvedFilenames: string[];
  summary: string;
  matchKinds: Record<string, FileMatchKind>;
};

function normalizeOrderedFiles(orderedFiles: OrderedFiles[] | undefined): string[] {
  if (!Array.isArray(orderedFiles) || orderedFiles.length === 0) {
    return [];
  }

  return [...orderedFiles]
    .sort((left, right) => left.orderNumber - right.orderNumber)
    .map((file) => file.filename)
    .filter((filename): filename is string => typeof filename === 'string' && filename.length > 0);
}

/** What the page shows of its files: the review plan's, the diff's, and the file in view. */
type PageFilesContext = Pick<
  AiContext,
  'orderedFiles' | 'codeContent' | 'activeFilename' | 'visibleFilename'
>;

function getViewableFilenames(context: PageFilesContext): string[] {
  const filenames = new Set<string>();

  for (const filename of [
    ...normalizeOrderedFiles(context.orderedFiles),
    ...(context.codeContent?.map((file) => file.filename) ?? []),
  ]) {
    if (typeof filename === 'string' && filename.length > 0) {
      filenames.add(filename);
    }
  }

  return Array.from(filenames);
}

function formatFileList(filenames: string[]): string {
  if (filenames.length === 0) {
    return 'no files';
  }

  if (filenames.length === 1) {
    return filenames[0] ?? 'unknown file';
  }

  return `${filenames.length} files`;
}

function buildSummary(result: {
  filenames: string[];
  unresolvedFilenames: string[];
  viewed: boolean;
}): string {
  const action = result.viewed ? 'viewed' : 'unviewed';
  const resolvedSummary =
    result.filenames.length > 0 ? `Marked ${formatFileList(result.filenames)} as ${action}.` : '';
  const unresolvedSummary =
    result.unresolvedFilenames.length > 0
      ? ` Could not find: ${result.unresolvedFilenames.join(', ')}.`
      : '';

  return `${resolvedSummary}${unresolvedSummary}`.trim() || 'No matching PR files found.';
}

/**
 * What a setPrFileViewed call resolves to against the PR page's files: the changed files it marks,
 * and those it could not find. Pure, so a pull request page's chat session resolves its
 * `set_pr_file_viewed` calls in the browser the same way (`src/components/chat/code-session/`).
 */
export function resolveSetPrFileViewedResult(
  context: PageFilesContext,
  input: SetPrFileViewedInput,
): SetPrFileViewedResult {
  const availableFilenames = getViewableFilenames(context);
  const requestedFilenames =
    input.filenames && input.filenames.length > 0
      ? input.filenames
      : [context.activeFilename ?? context.visibleFilename ?? ''];

  const filenames: string[] = [];
  const availableFilenameSet = new Set(availableFilenames);
  const filenameSet = new Set<string>();
  const unresolvedFilenames: string[] = [];
  const matchKinds: Record<string, FileMatchKind> = {};

  for (const requestedFilename of requestedFilenames) {
    const resolved =
      !input.filenames && requestedFilename
        ? { path: requestedFilename, matchKind: 'current' as const }
        : resolveChangedFilePathFromOrder(availableFilenames, requestedFilename);

    if (!resolved || !availableFilenameSet.has(resolved.path)) {
      unresolvedFilenames.push(requestedFilename || 'current file');
      continue;
    }

    if (!filenameSet.has(resolved.path)) {
      filenameSet.add(resolved.path);
      filenames.push(resolved.path);
      matchKinds[resolved.path] = resolved.matchKind;
    }
  }

  return {
    pendingAction: true,
    success: filenames.length > 0,
    viewed: input.viewed,
    filenames,
    unresolvedFilenames,
    matchKinds,
    summary: buildSummary({ filenames, unresolvedFilenames, viewed: input.viewed }),
  };
}

export function createSetPrFileViewedTool(context: AiContext) {
  return tool({
    inputSchema: setPrFileViewedSchema,
    description:
      'Mark one or more changed PR files as viewed or unviewed in the PR changes UI. Use only when the user explicitly asks to mark files viewed/unviewed. Omit filenames only for the current file.',
    execute: async (input: SetPrFileViewedInput): Promise<SetPrFileViewedResult> =>
      resolveSetPrFileViewedResult(context, input),
  });
}
